import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { US_STATES } from "@/lib/usStates";
import { toast } from "sonner";

export default function AuthPage({ mode }) {
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get("role") || "cook");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [stateCode, setStateCode] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const nav = useNavigate();

  const isLogin = mode === "login";
  const ref = params.get("ref") || "";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = isLogin
        ? await login(email, password)
        : await register({ email, password, name, role, ref, state: stateCode });
      toast.success(isLogin ? "Welcome back" : "Account created");
      if (user.role === "cook") nav("/cook");
      else if (user.role === "owner") nav("/owner");
      else if (user.role === "admin") nav("/admin");
      else nav("/browse");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-md mx-auto px-6 py-20">
        <div className="tick-label">{isLogin ? "Sign in" : "Create account"}</div>
        <h1 className="font-display font-black text-5xl tracking-tighter mt-2">
          {isLogin ? "Welcome back." : "Get cooking."}
        </h1>

        <form onSubmit={submit} className="mt-10 space-y-5" data-testid="auth-form">
          {!isLogin && (
            <>
              <div>
                <div className="tick-label mb-2">I am a</div>
                <div className="grid grid-cols-2 gap-0 border hairline">
                  {["cook", "owner"].map((r) => (
                    <button
                      key={r}
                      type="button"
                      data-testid={`role-${r}`}
                      onClick={() => setRole(r)}
                      className={`py-4 uppercase text-xs tracking-widest font-medium transition-colors ${
                        role === r ? "bg-[#FF3B30] text-white" : "text-neutral-400 hover:text-white"
                      }`}
                    >
                      {r === "cook" ? "Cook / Chef" : "Kitchen Host"}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="tick-label mb-2">Name</div>
                <input
                  data-testid="input-name"
                  className="w-full border hairline px-4 py-3 focus:outline-none focus:border-[#FF3B30]"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
              <div>
                <div className="tick-label mb-2">State</div>
                <select
                  data-testid="signup-state-select"
                  className="w-full border hairline px-4 py-3 bg-transparent focus:outline-none focus:border-[#FF3B30]"
                  value={stateCode}
                  onChange={(e) => setStateCode(e.target.value)}
                >
                  <option value="">Select state (optional)</option>
                  {US_STATES.map((s) => (
                    <option key={s.code} value={s.code}>{s.code} — {s.name}</option>
                  ))}
                </select>
              </div>
            </>
          )}
          <div>
            <div className="tick-label mb-2">Email</div>
            <input
              data-testid="input-email"
              type="email"
              className="w-full border hairline px-4 py-3 focus:outline-none focus:border-[#FF3B30]"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div>
            <div className="tick-label mb-2">Password</div>
            <input
              data-testid="input-password"
              type="password"
              className="w-full border hairline px-4 py-3 focus:outline-none focus:border-[#FF3B30]"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
            />
          </div>

          <button
            data-testid="auth-submit"
            type="submit"
            disabled={busy}
            className="btn-signal w-full disabled:opacity-50"
          >
            {busy ? "..." : isLogin ? "Sign in" : "Create account"}
          </button>

          <div className="tick-label text-center">
            {isLogin ? (
              <>No account? <Link data-testid="link-register" to="/register" className="text-[#FF3B30]">Sign up</Link></>
            ) : (
              <>Have one? <Link data-testid="link-login" to="/login" className="text-[#FF3B30]">Sign in</Link></>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
